import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { CalendarClock, Flag, PackageSearch, Store, UserPlus, Users } from 'lucide-react';
import { api, describeError } from '@/lib/api';
import { useAuthStore, useHasPlatformPermission } from '@/stores/auth';
import { SensitiveEventsWidget } from '@/components/SensitiveEventsWidget';
import { Badge, StatCard } from '@/components/ui';
import { subscriptionBadge } from '@/components/badge-labels';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';

interface TenantListItem {
  id: string;
  tradeName: string;
  ownerName: string;
  slug: string;
  isActive: boolean;
  subscriptionStatus: string;
  trialExpiresAt: string | null;
  createdAt: string;
}

interface Page<T> {
  items: T[];
  totalItems: number;
}

const DAY_MS = 86_400_000;

function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / DAY_MS);
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { operator } = useAuthStore();
  // O atalho só aparece para quem estende trial; a linha continua abrindo o tenant.
  const canExtendTrial = useHasPlatformPermission('platform.trial.extend');
  const [totalTenants, setTotalTenants] = useState<number | null>(null);
  const [totalActive, setTotalActive] = useState<number | null>(null);
  const [totalTrial, setTotalTrial] = useState<number | null>(null);
  const [recent, setRecent] = useState<TenantListItem[]>([]);
  const [trials, setTrials] = useState<TenantListItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get<Page<TenantListItem>>('/platform/tenants', { params: { page: 1, pageSize: 6 } }),
      api.get<Page<TenantListItem>>('/platform/tenants', {
        params: { page: 1, pageSize: 1, isActive: true },
      }),
      api.get<Page<TenantListItem>>('/platform/tenants', {
        params: { page: 1, pageSize: 100, subscriptionStatus: 'Trial' },
      }),
    ])
      .then(([all, active, trial]) => {
        if (cancelled) return;
        setTotalTenants(all.data.totalItems);
        setRecent(all.data.items);
        setTotalActive(active.data.totalItems);
        setTotalTrial(trial.data.totalItems);
        setTrials(
          trial.data.items
            .filter((t) => t.trialExpiresAt)
            .sort(
              (a, b) =>
                new Date(a.trialExpiresAt!).getTime() - new Date(b.trialExpiresAt!).getTime(),
            ),
        );
      })
      .catch((err) => !cancelled && setError(describeError(err)));
    return () => {
      cancelled = true;
    };
  }, []);

  const expiringSoon = trials.filter(
    (t) => t.trialExpiresAt && daysUntil(t.trialExpiresAt) <= 7,
  );

  return (
    <div className="page">
      {error && <div className="error-banner">{error}</div>}

      <div className="stat-grid">
        <StatCard
          label="Lojas cadastradas"
          value={totalTenants ?? '—'}
          icon={<Store size={22} />}
        />
        <StatCard
          label="Lojas ativas"
          value={totalActive ?? '—'}
          icon={<Users size={22} />}
        />
        <StatCard label="Em teste grátis" value={totalTrial ?? '—'} icon={<CalendarClock size={22} />} />
        <StatCard
          label="Testes grátis expirando (7d)"
          value={expiringSoon.length}
          delta={expiringSoon.length > 0 ? 'priorize o contato comercial' : 'nenhum nos próximos dias'}
          deltaTone={expiringSoon.length > 0 ? 'error' : 'neutral'}
          icon={<CalendarClock size={22} />}
        />
      </div>

      <div className="grid-main-aside">
        <div className="card card-flush">
          <div className="card-head">
            <div>
              <div className="card-title">Testes grátis expirando em breve</div>
              <div className="card-subtitle">Priorize o contato comercial</div>
            </div>
            <Link to="/tenants" style={{ fontSize: 13, fontWeight: 600 }}>Ver todos</Link>
          </div>
          <div className="table-scroll">
            <CardTable>
              <THead>
                <Tr>
                  <Th>Restaurante</Th>
                  <Th>Expira</Th>
                  <Th>Restam</Th>
                  <Th></Th>
                </Tr>
              </THead>
              <TBody>
                {expiringSoon.length === 0 && (
                  <Tr>
                    <Td colSpan={4} style={{ textAlign: 'center', padding: 24 }} className="muted">
                      Nenhum teste grátis expirando nos próximos 7 dias.
                    </Td>
                  </Tr>
                )}
                {expiringSoon.slice(0, 6).map((t) => {
                  const days = daysUntil(t.trialExpiresAt!);
                  return (
                    <Tr key={t.id} className="row-link" onClick={() => navigate(`/tenants/${t.id}`)}>
                      <Td heading>
                        <div style={{ fontWeight: 600 }}>{t.tradeName}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{t.ownerName}</div>
                      </Td>
                      <Td label="Expira" className="muted">
                        {new Date(t.trialExpiresAt!).toLocaleDateString('pt-BR')}
                      </Td>
                      <Td label="Restam">
                        <Badge tone={days <= 2 ? 'error' : 'warning'} dot>
                          {days <= 0 ? 'expirado' : `${days} dia${days === 1 ? '' : 's'}`}
                        </Badge>
                      </Td>
                      <Td action style={{ textAlign: 'right' }}>
                        {canExtendTrial && (
                          <Link to={`/tenants/${t.id}/trial`} style={{ fontWeight: 600, fontSize: 13 }}>
                            Estender →
                          </Link>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </TBody>
            </CardTable>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Atalhos</div>
            <div className="grid-2" style={{ gap: 10 }}>
              {operator?.role === 'Admin' && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => navigate('/operators')}
                >
                  <UserPlus size={16} /> Operador
                </button>
              )}
              {operator?.role === 'Admin' && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => navigate('/feature-flags')}
                >
                  <Flag size={16} /> Feature flag
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => navigate('/orders/search')}
              >
                <PackageSearch size={16} /> Pedido
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => navigate('/tenants')}
              >
                <Store size={16} /> Lojas
              </button>
            </div>
          </div>
          <SensitiveEventsWidget />
        </div>
      </div>

      <div className="card card-flush">
        <div className="card-head">
          <div className="card-title">Lojas recentes</div>
          <Link to="/tenants" style={{ fontSize: 13, fontWeight: 600 }}>Ver todos</Link>
        </div>
        <div className="table-scroll">
          <CardTable>
            <THead>
              <Tr>
                <Th>Restaurante</Th>
                <Th>Status</Th>
                <Th>Ativo</Th>
                <Th>Criado em</Th>
              </Tr>
            </THead>
            <TBody>
              {recent.map((t) => {
                const sub = subscriptionBadge(t.subscriptionStatus);
                return (
                  <Tr key={t.id} className="row-link" onClick={() => navigate(`/tenants/${t.id}`)}>
                    <Td heading>
                      <div style={{ fontWeight: 600 }}>{t.tradeName}</div>
                      <div className="muted mono" style={{ fontSize: 12 }}>{t.slug}</div>
                    </Td>
                    <Td label="Status">
                      <Badge tone={sub.tone} dot>{sub.label}</Badge>
                    </Td>
                    <Td label="Ativo">
                      {t.isActive ? (
                        <Badge tone="success">Sim</Badge>
                      ) : (
                        <Badge tone="error">Não</Badge>
                      )}
                    </Td>
                    <Td label="Criado em" className="muted">{new Date(t.createdAt).toLocaleDateString('pt-BR')}</Td>
                  </Tr>
                );
              })}
            </TBody>
          </CardTable>
        </div>
      </div>
    </div>
  );
}
